import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { toast } from '../../components/toast.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { digestPrefBody, notifyPrefBody } from '../../core/parity.js';

/**
 * Email and weekly-digest preferences. Every user, not admin-only.
 * @param {{ host: HTMLElement, me: object, productId?: string, dead: () => boolean }} ctx
 */
export function mountNotifications(ctx) {
  const state = { loading: true, fatal: '', mentions: true, reports: true, digest: true };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(4));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'Notifications unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: load }),
      }));
      return;
    }
    ctx.host.append(el('div', { class: 'set-stack' },
      el('div', { class: 'set-panel' },
        el('div', { class: 'set-panel-head' },
          el('div', { class: 'set-panel-title' }, el('span', {}, 'Email'))),
        el('p', { class: 'set-hint' }, 'Mentions email if you have been away about 10 minutes. Report-ready mail follows the toggle below.'),
        prefRow('Email mentions when away', state.mentions, (checked) => saveEmail('mentions', checked)),
        prefRow('Email when a report is ready', state.reports, (checked) => saveEmail('reports', checked))),
      el('div', { class: 'set-panel' },
        el('div', { class: 'set-panel-head' },
          el('div', { class: 'set-panel-title' }, el('span', {}, 'Weekly digest'))),
        el('p', { class: 'set-hint' }, 'The same switch as the digest pill on the dashboard.'),
        prefRow('Weekly digest', state.digest, saveDigest))));
  }

  async function load() {
    state.loading = true;
    state.fatal = '';
    paint();
    const jobs = [api('/auth/me')];
    if (ctx.productId) {
      jobs.push(api(`/insights?product_id=${encodeURIComponent(ctx.productId)}&skip_live=1`));
    }
    const [me, insights] = await Promise.all(jobs);
    if (ctx.dead()) return;
    state.loading = false;
    if (!me || me.error) {
      state.fatal = (me && me.error) || 'Could not load your notification settings';
      paint();
      return;
    }
    state.mentions = me.notify_email_mentions !== false;
    state.reports = me.notify_email_reports !== false;
    state.digest = !insights || insights.error || insights.digest_on !== false;
    paint();
  }

  async function saveEmail(key, checked) {
    const previous = key === 'mentions' ? state.mentions : state.reports;
    if (key === 'mentions') state.mentions = checked;
    else state.reports = checked;
    const result = await api('/notifications/prefs', {
      method: 'PUT',
      body: notifyPrefBody(key === 'mentions' ? { mentions: checked } : { reports: checked }),
    });
    if (!result || result.error) {
      if (key === 'mentions') state.mentions = previous;
      else state.reports = previous;
      toast((result && result.error) || 'Could not save that preference', 'error');
      paint();
    }
  }

  async function saveDigest(checked) {
    const previous = state.digest;
    state.digest = checked;
    const result = await api('/insights/digest-pref', { method: 'POST', body: digestPrefBody(checked) });
    if (!result || result.error) {
      state.digest = previous;
      toast((result && result.error) || 'Could not save digest preference', 'error');
      paint();
    }
  }

  load();
  return { dispose() {} };
}

function prefRow(label, checked, onChange) {
  return el('label', { class: 'set-pref' },
    el('span', {}, label),
    el('span', { class: 'set-switch' },
      el('input', {
        type: 'checkbox',
        checked: checked ? true : null,
        'aria-label': label,
        onChange: (event) => onChange(!!event.target.checked),
      }),
      el('span', { class: 'set-switch-ui', 'aria-hidden': 'true' })));
}
